import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { confirmAlert } from 'react-confirm-alert'
import 'react-confirm-alert/src/react-confirm-alert.css'
import { useMutation, useQuery } from '@tanstack/react-query'
import { taxisAPI } from '../../../../api/vehicle/taxisAPI'
import { Taxi } from '../../../../api/vehicle/taxisAPI.types'

import VehicleDisplayName from 'components/Common/VehicleDisplayName/VehicleDisplayName'
import { useSupplierState } from '../../../../Pages/Supplier/SupplierContext'
import TwoChoiceDialog from '../../../Dialogs/TwoChoiceDialog/TwoChoiceDialog'

interface Props {
  handleClick: Function
  backToSummaryFunction: Function
}
interface DeleteMutation {
  taxiId: number
}

const formatRange = (from?: number, to?: number) => {
  const f = from ?? 0
  return to ? `${f}–${to}` : `${f}+`
}

const MileageRanges: FC<{ taxiId: number }> = ({ taxiId }) => {
  const { data } = useQuery({
    queryKey: ['taxiSummaryChargeRates', taxiId],
    queryFn: () => taxisAPI.getChargeRatesByTaxiId(taxiId),
    refetchOnWindowFocus: false,
  })
  const rates = data?.data.value ?? []
  const sorted = [...rates].sort(
    (a, b) => (a.fromMile ?? 0) - (b.fromMile ?? 0),
  )

  if (sorted.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No mileage ranges
      </Typography>
    )
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {sorted.map((rate) => (
        <Chip
          key={rate.id ?? `${rate.fromMile}-${rate.toMile}`}
          label={`${formatRange(rate.fromMile, rate.toMile)} mi · £${Number(
            rate.pricePerMile ?? 0,
          ).toFixed(2)}`}
        />
      ))}
    </Stack>
  )
}

const TaxiRow: FC<{
  taxi: Taxi
  onClick: () => void
  onDelete: () => void
}> = ({ taxi, onClick, onDelete }) => {
  const name = VehicleDisplayName({ vehicleDetails: taxi.vehicleDetails })
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        px: 3,
        py: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {taxi.vehicleDetails.displayName && (
        <Box
          component="img"
          src={taxi.vehicleDetails.icon}
          alt={name}
          sx={{ height: 54, width: 84, objectFit: 'contain' }}
        />
      )}
      <Box sx={{ minWidth: 240 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {taxi.vehicleDetails.seats} seats · {taxi.fleetSize} vehicles ·{' '}
          {taxi.averageAge}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 270 }}>
        <Typography variant="body2" color="text.secondary">
          Min fare
        </Typography>
        <Typography variant="body1">
          £{Number(taxi?.minimumFare?.price ?? 0).toFixed(2)}
          {taxi?.waitingTime?.chargingPeriod?.price
            ? ` · Wait £${Number(
                taxi.waitingTime.chargingPeriod.price,
              ).toFixed(2)}/${taxi.waitingTime.chargingPeriod.period}m`
            : ''}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.75 }}
        >
          Mileage ranges
        </Typography>
        <MileageRanges taxiId={taxi.id!} />
      </Box>
      <Tooltip title="Delete vehicle">
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const TaxiSummary: FC<Props> = (props) => {
  const { handleClick, backToSummaryFunction } = props
  const { taxis } = useSupplierState()

  const { mutateAsync: mutate } = useMutation({
    mutationFn: (data: DeleteMutation) => taxisAPI.deleteTaxi(data.taxiId),
  })

  const deleteTaxi = (taxiId: number) => {
    mutate({ taxiId }).finally(() => backToSummaryFunction())
  }

  const deleteConfirmation = (taxiId: number) => {
    confirmAlert({
      // eslint-disable-next-line react/display-name
      customUI: ({ onClose }) => (
        <TwoChoiceDialog
          title={'Confirm Deletion'}
          message={'Are you sure you want to delete this vehicle?'}
          onClose={onClose}
          functionCall={() => deleteTaxi(taxiId)}
        />
      ),
    })
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {taxis?.map((taxi) => (
        <TaxiRow
          key={taxi.id}
          taxi={taxi}
          onClick={() => handleClick(taxi.id)}
          onDelete={() => deleteConfirmation(taxi.id!)}
        />
      ))}
    </Stack>
  )
}

export default TaxiSummary
